import { formatDistanceToNow } from 'date-fns';
import {
  Badge,
  Box,
  Flex,
  Pagination,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
} from '@optiaxiom/react';

import { EmptyPage } from '@frontend/components';
import { SpecializedAgentExecution } from '@frontend/types/specialized-agent';

import { paginationWrapper } from './SpecializedAgentExecutionsTable.css';

type SpecializedAgentExecutionsTableProps = {
  executions: SpecializedAgentExecution[];
  pagination?: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  isLoading: boolean;
  error: Error | null;
  onExecutionSelect: (execution: SpecializedAgentExecution) => void;
  selectedExecutionId?: string;
  onPageChange: (page: number) => void;
};

const getStatusBadgeIntent = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'running':
    case 'pending':
      return 'warning';
    default:
      return 'neutral';
  }
};

const formatTimestamp = (timestamp: string) => {
  // Ensure timestamp is treated as UTC by appending 'Z' if not present
  const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  const date = new Date(utcTimestamp);

  // Return relative time (e.g., "2 minutes ago")
  return formatDistanceToNow(date, { addSuffix: true });
};

const formatFullTimestamp = (timestamp: string) => {
  // Ensure timestamp is treated as UTC by appending 'Z' if not present
  const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  const date = new Date(utcTimestamp);

  // Format as local date and time
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
};

const formatExecutionTime = (executionTime: number) => {
  return `${executionTime.toFixed(2)}s`;
};

const formatEvaluationScore = (score: number | null | undefined) => {
  return score != null ? `${score}%` : '—';
};

const formatParameters = (parameters: Record<string, unknown>) => {
  const jsonString = JSON.stringify(parameters);
  // Truncate if longer than 50 characters
  if (jsonString.length > 50) {
    return jsonString.substring(0, 47) + '...';
  }
  return jsonString;
};

const getRowInlineStyle = (status: string, isSelected: boolean) => {
  const baseStyle = {
    cursor: 'pointer' as const,
    backgroundColor: isSelected ? 'var(--colors-bg-secondary)' : undefined,
  };

  // Add visual styles for running executions
  switch (status.toLowerCase()) {
    case 'pending':
    case 'running':
      return {
        ...baseStyle,
        backgroundColor: isSelected ? 'var(--colors-bg-secondary)' : 'rgba(245, 158, 11, 0.1)',
        background: isSelected
          ? 'var(--colors-bg-secondary)'
          : 'linear-gradient(90deg, rgba(245, 158, 11, 0.05) 0%, rgba(245, 158, 11, 0.3) 50%, rgba(245, 158, 11, 0.05) 100%)',
        backgroundSize: '200% 100%',
        animation: 'gradient-shimmer 1.5s ease-in-out infinite',
      };
    default:
      return {
        ...baseStyle,
        backgroundColor: isSelected ? 'var(--colors-bg-secondary)' : 'rgba(0, 0, 0, 0.02)',
      };
  }
};

export function SpecializedAgentExecutionsTable({
  executions,
  pagination,
  isLoading,
  error,
  onExecutionSelect,
  selectedExecutionId,
  onPageChange,
}: SpecializedAgentExecutionsTableProps) {
  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" h="224">
        <Spinner size="lg">Loading executions...</Spinner>
      </Flex>
    );
  }

  if (error) {
    return <EmptyPage title="Failed to load executions" description={error.message} />;
  }

  if (executions.length === 0) {
    return (
      <EmptyPage
        title="No executions found"
        description="Agent executions will appear here as they are performed."
      />
    );
  }

  return (
    <Flex flexDirection="column" gap="16">
      <style>{`
        @keyframes gradient-shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Timestamp</TableHeaderCell>
            <TableHeaderCell>Parameters</TableHeaderCell>
            <TableHeaderCell>Evaluation Score</TableHeaderCell>
            <TableHeaderCell>Execution Time</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {executions.map(execution => (
            <TableRow
              key={execution.id}
              onClick={() => onExecutionSelect(execution)}
              style={getRowInlineStyle(execution.status, selectedExecutionId === execution.id)}
              data-status={execution.status}
            >
              <TableCell>
                <Text fontSize="md" title={formatFullTimestamp(execution.created_at)}>
                  {formatTimestamp(execution.created_at)}
                </Text>
              </TableCell>
              <TableCell>
                <Text
                  fontSize="md"
                  fontFamily="mono"
                  title={JSON.stringify(execution.parameters, null, 2)}
                >
                  {formatParameters(execution.parameters)}
                </Text>
              </TableCell>
              <TableCell>
                <Text fontSize="md">{formatEvaluationScore(execution.evaluation_score)}</Text>
              </TableCell>
              <TableCell>
                <Text fontSize="md">{formatExecutionTime(execution.execution_time)}</Text>
              </TableCell>
              <TableCell>
                <Badge intent={getStatusBadgeIntent(execution.status)}>
                  {execution.status.toLowerCase() === 'pending' ? 'Running' : execution.status}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      {pagination && pagination.total_pages > 0 && (
        <Flex justifyContent="center" alignItems="center" flexDirection="column" gap="8" mt="16">
          <Text fontSize="sm" color="fg.secondary">
            Showing {executions.length} of {pagination.total_count} executions
          </Text>
          <Box className={paginationWrapper}>
            <Pagination
              page={pagination.page}
              total={pagination.total_pages}
              onPageChange={onPageChange}
            />
          </Box>
        </Flex>
      )}
    </Flex>
  );
}
